import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

export const useGuestSelection = ({ adults, children, maxOccupancy, maxAdults, maxChildren, maxChildAge }) => {
    const { t } = useI18n()

    const ageOptions = computed(() => {
        const maxAge = Math.max(0, Number(maxChildAge.value) || 0)
        return Array.from({ length: maxAge + 1 }, (_, i) => ({ value: i, label: String(i) }))
    })

    const childrenList = computed(() => children.value ?? [])
    const childrenCount = computed(() => childrenList.value.length)

    const maxAdultsAllowed = computed(() => {
        const byTotal = Math.max(1, Number(maxOccupancy.value) - childrenCount.value)
        const byAdults = Number(maxAdults.value)
        return Math.max(1, Math.min(byTotal, byAdults))
    })

    const maxChildrenAllowed = computed(() => {
        const byTotal = Math.max(0, Number(maxOccupancy.value) - Number(adults.value || 1))
        const byChildren = Number(maxChildren.value)
        return Math.max(0, Math.min(byTotal, byChildren))
    })

    const clampAdults = () => {
        const max = maxAdultsAllowed.value
        if (Number(adults.value) < 1) {
            adults.value = 1
            return
        }
        if (Number(adults.value) > max) {
            adults.value = max
        }
    }

    watch(maxAdultsAllowed, clampAdults, { immediate: true })
    watch(childrenCount, clampAdults)

    watch(
        maxChildrenAllowed,
        (max) => {
            if (childrenCount.value > max) {
                children.value = childrenList.value.slice(0, max)
            }
        },
        { immediate: true }
    )

    const onChildrenCountChange = (newCount) => {
        const current = childrenCount.value

        if (newCount > current) {
            children.value = [...childrenList.value, ...Array.from({ length: newCount - current }, () => '')]
            return
        }

        if (newCount < current) {
            children.value = childrenList.value.slice(0, newCount)
        }
    }

    const displayValue = computed(() => {
        const parts = []

        if (adults.value > 0) {
            parts.push(t('adult', Number(adults.value)))
        }

        if (childrenCount.value > 0) {
            parts.push(t('child', childrenCount.value))
        }

        return parts.join(' - ')
    })

    return {
        ageOptions,
        childrenList,
        childrenCount,
        maxAdultsAllowed,
        maxChildrenAllowed,
        onChildrenCountChange,
        displayValue
    }
}
